import React, { useEffect, useState, useCallback, useRef, lazy, Suspense } from 'react';
import { flushSync } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Box, Button, Typography, List, ListItem, ListItemText, Divider, IconButton, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions, TextField } from '@mui/material';
import { Add, Menu as MenuIcon, Edit, Delete, Favorite, FavoriteBorder, Search } from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import { useAppStore } from '@/store';
import { createConversation, updateConversation, deleteConversation, listConversations, selectConversation } from '../api/conversations';
import { Conversation, DEFAULT_DATA_SOURCES } from '@/models/conversation';
import { formatNewChatTitle } from '@/utils/dateUtils';
import AgentSection from './AgentSection';
import { SectionHeader } from './SidebarComponents';
import { queryKeys } from '@/lib/queryKeys';
import { invalidateConversationCollections } from '@/lib/queryInvalidation';
import { useConversationsQuery } from '@/features/conversations/hooks/useConversationsQuery';

// Bossard Blue colors
const BOSSARD_BLUE = '#007ac3';
const BOSSARD_BLUE_10 = 'rgba(0, 122, 195, 0.1)';

// Lazy load the delete dialog to avoid chunk loading issues
const DeleteConfirmationDialog = lazy(() => import('./DeleteConfirmationDialog'));

const SidebarContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'open' && prop !== 'isMobile',
})<{
  open: boolean;
  isMobile?: boolean;
}>(({ theme, open, isMobile }) => ({  
  background: theme.palette.mode === 'light' ? '#eeeeee' : '#444444',
  backdropFilter: 'blur(10px)',
  borderTopRightRadius: theme.shape.borderRadius,
  borderBottomRightRadius: theme.shape.borderRadius,
  borderTopLeftRadius: 0,
  borderBottomLeftRadius: 0,
  // On mobile: constant 80% width for transform. On desktop: constant 30% width, but show as 50px when closed
  width: isMobile 
    ? '80%'
    : (open ? '30%' : 50), // Desktop: back to changing width for proper collapsed state
  minWidth: isMobile 
    ? 280 
    : (open ? 340 : 50), // Desktop: show 50px when closed
  maxWidth: isMobile 
    ? '80%' 
    : (open ? 450 : 50), // Desktop: max 50px when closed
  // On mobile, use top + bottom so we can account for iOS safe-area insets (home indicator).
  // Avoid using 100vh which can include the browser chrome on iOS and cause content to be
  // rendered under the navigation controls.
  height: isMobile ? 'auto' : '100%',
  display: 'flex',
  flexDirection: 'column',
  padding: open ? theme.spacing(1) : (isMobile ? 0 : theme.spacing(1, 0.5)),
  boxShadow: theme.palette.mode === 'light' 
    ? '0 8px 16px 0 #777777' 
    : '0 8px 16px 0 rgba(0,0,0,0.3)',
  // Desktop: animate width smoothly. Mobile: animate transform only
  transition: isMobile
    ? 'transform 0.3s ease, padding 0.3s ease'
    : 'width 0.3s ease, min-width 0.3s ease, max-width 0.3s ease, padding 0.3s ease',
  // On mobile: fixed position. On desktop: relative position
  position: isMobile ? 'fixed' : 'relative',
  left: isMobile ? 0 : 'auto',
  top: isMobile ? 56 : 'auto', // Start below header (56px) on mobile
  bottom: isMobile ? 'env(safe-area-inset-bottom)' : 'auto',
  // Only mobile uses transform to slide off-screen
  transform: isMobile 
    ? (!open ? 'translateX(-100%)' : 'translateX(0)') // Mobile: slide completely off
    : 'translateX(0)', // Desktop: no transform, just width change
  zIndex: isMobile ? 1200 : 10, // Higher z-index on mobile
  overflowX: 'hidden', // Prevent horizontal overflow during slide
  overflowY: 'auto', // Allow vertical scrolling for content
  // Ensure content isn't hidden behind the iOS home indicator
  paddingBottom: isMobile ? 'env(safe-area-inset-bottom)' : undefined,
}));

const commonMenuStyles = {
  boxShadow: 6,
  bgcolor: (theme: any) => theme.palette.mode === 'light' ? 'background.paper' : 'background.paper',
  borderRadius: '15px',
  minWidth: 180,
  // border: '1px solid',
  // borderColor: 'primary.main',
  marginLeft: '2%',
  '& .MuiMenuItem-root': {
    // color: 'primary.main', // Bossard Blue color for text
    '&:hover': {
      bgcolor: (theme: any) => theme.palette.mode === 'light' ? 'rgba(0, 0, 0, 0.08)' : 'rgba(255, 255, 255, 0.08)' // Theme-aware hover state
    }
  }
};

const MAX_VISIBLE = 10;

// Using SectionHeader from SidebarComponents.tsx

// User type definition
interface User {
  name?: string;
  username?: string; 
  picture?: string;
  isAdmin?: boolean;
  sub?: string;
  idTokenClaims?: { 
    name?: string; 
    preferred_username?: string; 
    picture?: string;
    sub?: string;
  };
}

interface SidebarProps {
  user?: User | null;
  hasModelAccess?: boolean;
}

interface ConversationListProps {
  conversations: Conversation[];
  selectedConversation: Conversation | null;
  onSelect: (conversation: Conversation) => void;
  onRename: (conversation: Conversation) => void;
  onDelete: (conversation: Conversation) => void;
  onToggleFavorite: (conversation: Conversation) => void;
  type: 'recents' | 'favorites'; // Used to differentiate sections
  hasModelAccess?: boolean;
  maxVisible?: number;
  showAllHistoryLink?: boolean;
  onShowAllHistory?: () => void;
}

// Helper function to get date group for a conversation
const getDateGroup = (conversation: Conversation): string => {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  
  // Calculate the start of the current week (Monday)
  const startOfWeek = new Date(today);
  const dayOfWeek = today.getDay(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday
  // Adjust to get Monday: if Sunday (0), go back 6 days; otherwise go back (dayOfWeek - 1) days
  const daysToMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
  startOfWeek.setDate(today.getDate() - daysToMonday);
  
  const monthAgo = new Date(today);
  monthAgo.setMonth(monthAgo.getMonth() - 1);

  // Use updated_date if available, otherwise use created_date
  const dateToCheck = conversation.updated_date || conversation.created_date;
  
  if (!dateToCheck) {
    return 'Older chats';
  }

  try {
    const convDate = new Date(dateToCheck);
    const convDateOnly = new Date(convDate.getFullYear(), convDate.getMonth(), convDate.getDate());

    if (convDateOnly >= startOfWeek) {
      return 'This week';
    } else if (convDateOnly > monthAgo) {
      return 'This month';
    } else {
      return 'Older chats';
    }
  } catch (e) {
    // Fallback if parsing fails
    return 'Older chats';
  }
};

const ConversationList: React.FC<ConversationListProps> = ({
  conversations,
  selectedConversation,
  onSelect,
  onRename,
  onDelete,
  onToggleFavorite,
  type,
  hasModelAccess = true,
  maxVisible = MAX_VISIBLE,
  showAllHistoryLink = false,
  onShowAllHistory
}) => {
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  
  // For favorites, don't group by date
  if (type === 'favorites') {
    const limitedConversations = conversations.slice(0, maxVisible);
    const showLink = showAllHistoryLink && conversations.length > maxVisible;
    
    return (
      <List dense disablePadding sx={{ 
        mb: 1
      }}>
        {limitedConversations.map((c) => {
          const isSelected = selectedConversation?.id === c.id;
          const isHovered = hoveredId === c.id;
          const isFavorite = c.is_favourite;
          const isResearcherConversation = c.agent_key === 'researcher';
          
          return (
            <ListItem
              key={c.id}
              component="div"
              onMouseEnter={() => setHoveredId(c.id)}
              onMouseLeave={() => setHoveredId(null)}
              sx={{
                borderRadius: 1,
                bgcolor: isSelected 
                  ? BOSSARD_BLUE 
                  : isHovered 
                    ? BOSSARD_BLUE_10 
                    : 'transparent',
                mb: 0.5,
                px: 2,
                py: 0.75,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                opacity: hasModelAccess ? 1 : 0.5,
                pointerEvents: hasModelAccess ? 'auto' : 'none',
                cursor: hasModelAccess ? 'pointer' : 'default',
                transition: 'background-color 0.2s',
              }}
              onClick={() => {
                onSelect(c);
                const setCurrentFeature = useAppStore.getState().setCurrentFeature;
                setCurrentFeature(null);
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', flex: 1, minWidth: 0, mr: 1 }}>
                {isResearcherConversation && (
                  <Typography
                    component="span"
                    aria-label="Researcher conversation"
                    sx={{
                      fontSize: 14,
                      mr: 0.75,
                      lineHeight: 1,
                      flexShrink: 0,
                    }}
                  >
                    🔬
                  </Typography>
                )}
                <ListItemText
                  primary={c.title}
                  primaryTypographyProps={{            
                    fontFamily: 'Arial, sans-serif',
                    fontSize: 14,
                    fontWeight: isSelected ? 600 : 400,
                    noWrap: true,
                    color: isSelected ? '#ffffff' : 'text.primary'
                  }}
                  sx={{ m: 0, minWidth: 0 }}
                />
              </Box>
              {/* Action icons - visible on hover or always for selected */}
              <Box 
                sx={{ 
                  display: 'flex', 
                  alignItems: 'center',
                  gap: 0.25,
                  opacity: 1,
                }}
              >
                <Tooltip title="Rename" arrow>
                  <IconButton 
                    size="small" 
                    onClick={(e) => {
                      e.stopPropagation();
                      onRename(c);
                    }}
                    sx={{ 
                      p: 0.5,
                      opacity: isHovered || isSelected ? 1 : 0,
                      pointerEvents: isHovered || isSelected ? 'auto' : 'none',
                      transition: 'opacity 0.2s',
                      color: isSelected ? '#ffffff' : 'text.secondary',
                      '&:hover': { 
                        bgcolor: isSelected ? 'rgba(255,255,255,0.2)' : BOSSARD_BLUE_10 
                      }
                    }}
                  >
                    <Edit sx={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Delete" arrow>
                  <IconButton 
                    size="small" 
                    onClick={(e) => {
                      e.stopPropagation();
                      onDelete(c);
                    }}
                    sx={{ 
                      p: 0.5,
                      opacity: isHovered || isSelected ? 1 : 0,
                      pointerEvents: isHovered || isSelected ? 'auto' : 'none',
                      transition: 'opacity 0.2s',
                      color: isSelected ? '#ffffff' : 'text.secondary',
                      '&:hover': { 
                        bgcolor: isSelected ? 'rgba(255,255,255,0.2)' : BOSSARD_BLUE_10 
                      }
                    }}
                  >
                    <Delete sx={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>
                <Tooltip title={isFavorite ? "Remove from favorites" : "Add to favorites"} arrow>
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      onToggleFavorite(c);
                    }}
                    sx={{
                      p: 0.5,
                      opacity: isHovered || isSelected || isFavorite ? 1 : 0,
                      pointerEvents: isHovered || isSelected || isFavorite ? 'auto' : 'none',
                      transition: 'opacity 0.2s',
                      color: isSelected ? '#ffffff' : (isFavorite ? BOSSARD_BLUE : 'text.secondary'),
                      '&:hover': {
                        bgcolor: isSelected ? 'rgba(255,255,255,0.2)' : BOSSARD_BLUE_10
                      }
                    }}
                  >
                    {isFavorite ? <Favorite sx={{ fontSize: 16 }} /> : <FavoriteBorder sx={{ fontSize: 16 }} />}
                  </IconButton>
                </Tooltip>
              </Box>
            </ListItem>
          );
        })}
        {/* All Chat History link */}
        {showLink && (
          <Box sx={{ px: 2, py: 1 }}>
            <Typography
              component="a"
              onClick={(e) => {
                e.preventDefault();
                onShowAllHistory?.();
              }}
              sx={{
                fontFamily: 'Arial, sans-serif',
                fontSize: 14,
                color: BOSSARD_BLUE,
                textDecoration: 'none',
                cursor: 'pointer',
                '&:hover': {
                  textDecoration: 'underline'
                }
              }}
            >
              All Chat History
            </Typography>
          </Box>
        )}
      </List>
    );
  }
  
  // Group conversations by date for recents
  const groupedConversations: { [key: string]: Conversation[] } = {};
  const dateOrder = ['This week', 'This month', 'Older chats'];

  // Track total count for limiting
  let totalCount = 0;
  const limitedConversations = conversations.slice(0, maxVisible);

  limitedConversations.forEach((c) => {
    const dateGroup = getDateGroup(c);
    if (!groupedConversations[dateGroup]) {
      groupedConversations[dateGroup] = [];
    }
    groupedConversations[dateGroup].push(c);
    totalCount++;
  });

  // Sort conversations within each date group by updated_date descending
  Object.keys(groupedConversations).forEach((dateGroup) => {
    groupedConversations[dateGroup].sort((a, b) => {
      const dateA = new Date(a.updated_date || a.created_date || 0).getTime();
      const dateB = new Date(b.updated_date || b.created_date || 0).getTime();
      return dateB - dateA; // Newest first
    });
  });

  const sortedDateGroups = Object.keys(groupedConversations).sort((a, b) => {
    const aIndex = dateOrder.indexOf(a);
    const bIndex = dateOrder.indexOf(b);
    return (aIndex === -1 ? 999 : aIndex) - (bIndex === -1 ? 999 : bIndex);
  });

  const showLink = showAllHistoryLink && conversations.length > maxVisible;

  return (
    <List dense disablePadding sx={{ 
      mb: 1
    }}>
      {sortedDateGroups.map((dateGroup, groupIndex) => (
        <Box key={dateGroup}>
          {groupIndex > 0 && (
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 1.5, 
              px: 2, 
              my: 1 
            }}>
              <Box sx={{ 
                flex: 1, 
                height: '1px', 
                bgcolor: (theme) => theme.palette.mode === 'light' 
                  ? 'rgba(0, 0, 0, 0.12)' 
                  : 'rgba(255, 255, 255, 0.12)' 
              }} />
            </Box>
          )}
          <Typography 
            sx={{ 
              px: 0, 
              py: 0.5,
              display: 'block',
              fontFamily: '"Dinot Black", Arial, sans-serif',
              fontWeight: 900,
              fontSize: '0.75rem',
              color: BOSSARD_BLUE,
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              mb: 0.5
            }}
          >
            {dateGroup}
          </Typography>
          {groupedConversations[dateGroup].map((c) => {
            const isSelected = selectedConversation?.id === c.id;
            const isHovered = hoveredId === c.id;
            const isFavorite = c.is_favourite;
            const isResearcherConversation = c.agent_key === 'researcher';
            
            return (
              <ListItem
                key={c.id}
                component="div"
                onMouseEnter={() => setHoveredId(c.id)}
                onMouseLeave={() => setHoveredId(null)}
                sx={{
                  borderRadius: 1,
                  bgcolor: isSelected 
                    ? BOSSARD_BLUE 
                    : isHovered 
                      ? BOSSARD_BLUE_10 
                      : 'transparent',
                  mb: 0.5,
                  px: 2,
                  py: 0.75,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  opacity: hasModelAccess ? 1 : 0.5,
                  pointerEvents: hasModelAccess ? 'auto' : 'none',
                  cursor: hasModelAccess ? 'pointer' : 'default',
                  transition: 'background-color 0.2s',
                }}
                onClick={() => {
                  onSelect(c);
                  const setCurrentFeature = useAppStore.getState().setCurrentFeature;
                  setCurrentFeature(null);
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', flex: 1, minWidth: 0, mr: 1 }}>
                  {isResearcherConversation && (
                    <Typography
                      component="span"
                      aria-label="Researcher conversation"
                      sx={{
                        fontSize: 14,
                        mr: 0.75,
                        lineHeight: 1,
                        flexShrink: 0,
                      }}
                    >
                      🔬
                    </Typography>
                  )}
                  <ListItemText
                    primary={c.title}
                    primaryTypographyProps={{            
                      fontFamily: 'Arial, sans-serif',
                      fontSize: 14,
                      fontWeight: isSelected ? 600 : 400,
                      noWrap: true,
                      color: isSelected ? '#ffffff' : 'text.primary'
                    }}
                    sx={{ m: 0, minWidth: 0 }}
                  />
                </Box>
                {/* Action icons - visible on hover or always for selected */}
                <Box 
                  sx={{ 
                    display: 'flex', 
                    alignItems: 'center',
                    gap: 0.25,
                    opacity: 1,
                  }}
                >
                  <Tooltip title="Rename" arrow>
                    <IconButton 
                      size="small" 
                      onClick={(e) => {
                        e.stopPropagation();
                        onRename(c);
                      }}
                      sx={{ 
                        p: 0.5,
                        opacity: isHovered || isSelected ? 1 : 0,
                        pointerEvents: isHovered || isSelected ? 'auto' : 'none',
                        transition: 'opacity 0.2s',
                        color: isSelected ? '#ffffff' : 'text.secondary',
                        '&:hover': { 
                          bgcolor: isSelected ? 'rgba(255,255,255,0.2)' : BOSSARD_BLUE_10 
                        }
                      }}
                    >
                      <Edit sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete" arrow>
                    <IconButton 
                      size="small" 
                      onClick={(e) => {
                        e.stopPropagation();
                        onDelete(c);
                      }}
                      sx={{ 
                        p: 0.5,
                        opacity: isHovered || isSelected ? 1 : 0,
                        pointerEvents: isHovered || isSelected ? 'auto' : 'none',
                        transition: 'opacity 0.2s',
                        color: isSelected ? '#ffffff' : 'text.secondary',
                        '&:hover': { 
                          bgcolor: isSelected ? 'rgba(255,255,255,0.2)' : BOSSARD_BLUE_10 
                        }
                      }}
                    >
                      <Delete sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={isFavorite ? "Remove from favorites" : "Add to favorites"} arrow>
                    <IconButton 
                      size="small" 
                      onClick={(e) => {
                        e.stopPropagation();
                        onToggleFavorite(c);
                      }}
                      sx={{ 
                        p: 0.5,
                        opacity: isHovered || isSelected || isFavorite ? 1 : 0,
                        pointerEvents: isHovered || isSelected || isFavorite ? 'auto' : 'none',
                        transition: 'opacity 0.2s',
                        color: isFavorite 
                          ? (isSelected ? '#ffffff' : BOSSARD_BLUE) 
                          : (isSelected ? '#ffffff' : 'text.secondary'),
                        '&:hover': { 
                          bgcolor: isSelected ? 'rgba(255,255,255,0.2)' : BOSSARD_BLUE_10 
                        }
                      }}
                    >
                      {isFavorite ? <Favorite sx={{ fontSize: 16 }} /> : <FavoriteBorder sx={{ fontSize: 16 }} />}
                    </IconButton>
                  </Tooltip>
                </Box>
              </ListItem>
            );
          })}
        </Box>
      ))}
      {/* All Chat History link */}
      {showLink && (
        <Box sx={{ px: 2, py: 1 }}>
          <Typography
            component="a"
            onClick={(e) => {
              e.preventDefault();
              onShowAllHistory?.();
            }}
            sx={{
              fontFamily: 'Arial, sans-serif',
              fontSize: 14,
              color: BOSSARD_BLUE,
              textDecoration: 'none',
              cursor: 'pointer',
              '&:hover': {
                textDecoration: 'underline'
              }
            }}
          >
            All Chat History
          </Typography>
        </Box>
      )}
    </List>
  );
};

const ConversationSection: React.FC<{
  type: 'recents' | 'favorites';
  conversations: Conversation[];
  isOpen: boolean;
  onToggle: () => void;
  selectedConversation: Conversation | null;
  onSelect: (conversation: Conversation) => void;
  onRename: (conversation: Conversation) => void;
  onDelete: (conversation: Conversation) => void;
  onToggleFavorite: (conversation: Conversation) => void;
  hasModelAccess?: boolean;
  showAllHistoryLink?: boolean;
  onShowAllHistory?: () => void;
}> = ({ 
  type, 
  conversations, 
  isOpen, 
  onToggle, 
  selectedConversation, 
  onSelect, 
  onRename, 
  onDelete, 
  onToggleFavorite, 
  hasModelAccess = true,
  showAllHistoryLink = false,
  onShowAllHistory
}) => (
  <Box>
    <SectionHeader
      title={type === 'recents' ? 'Chat' : 'Favorites'}
      isOpen={isOpen}
      onToggle={onToggle}
      disabled={!hasModelAccess}
    />
    {isOpen && (
      <ConversationList
        conversations={conversations}
        selectedConversation={selectedConversation}
        onSelect={onSelect}
        onRename={onRename}
        onDelete={onDelete}
        onToggleFavorite={onToggleFavorite}
        type={type}
        hasModelAccess={hasModelAccess}
        showAllHistoryLink={showAllHistoryLink}
        onShowAllHistory={onShowAllHistory}
      />
    )}
    <Divider sx={{ bgcolor: (theme) => theme.palette.mode === 'light' ? 'rgba(0, 0, 0, 0.12)' : 'rgba(255, 255, 255, 0.12)', my: 2 }} />
  </Box>
);

interface FeatureOption {
  id: string;
  name: string;
  icon: string;
}

// Hardcoded fallback features (used if backend features are not available)
const FALLBACK_FEATURES: FeatureOption[] = [
  { id: 'generate-image', name: 'Generate image', icon: '🖼️' },
  { id: 'generate-video', name: 'Generate video', icon: '🎬' },
  { id: 'translate-document', name: 'Translate document', icon: '📄' },
  { id: 'translate-text', name: 'Translate Text', icon: '🔠' }
];

const FeaturesSection: React.FC<{
  isOpen: boolean;
  onToggle: () => void;
  user?: { name?: string; isAdmin?: boolean; picture?: string; };
  hasModelAccess?: boolean;
  onFeatureSelect?: () => void; // Callback for when a feature is selected
}> = ({ isOpen, onToggle, hasModelAccess = true, onFeatureSelect }) => {
  const setMessage = useAppStore(s => s.setMessage);
  const setCurrentFeature = useAppStore(s => s.setCurrentFeature);
  const currentFeature = useAppStore(s => s.currentFeature);
  const setSelectedConversation = useAppStore(s => s.setSelectedConversation);
  const setSelectedAgent = useAppStore(s => s.setSelectedAgent);
  const featureList = useAppStore(s => s.featureList);
  
  const handleFeatureSelect = (feature: FeatureOption) => {
    if (!hasModelAccess) return;
    setCurrentFeature(feature.id);
    setMessage(''); // Clear the message when switching features
    setSelectedConversation(null); // Clear conversation selection when selecting feature
    setSelectedAgent(null); // Clear agent selection when selecting feature
    
    // Call the callback if provided (for mobile sidebar closing)
    if (onFeatureSelect) {
      onFeatureSelect();
    }
  };
  
  // Use features from store if available, otherwise fallback to hardcoded features
  const displayFeatures = featureList.length > 0 ? featureList : FALLBACK_FEATURES;

  return (
    <Box>      <SectionHeader
        title="Features"
        isOpen={isOpen}
        onToggle={onToggle}
        disabled={!hasModelAccess}
      />
      
      {isOpen && (
        <List dense disablePadding sx={{ 
          mb: 1
        }}>
          {displayFeatures.map((feature) => (
            <ListItem
              key={feature.id}
              component="div"
              sx={{
                borderRadius: 1,
                color: 'primary.main',
                bgcolor: currentFeature === feature.id 
                  ? BOSSARD_BLUE
                  : 'transparent',
                fontWeight: currentFeature === feature.id ? 700 : 500,
                mb: 0.5,
                px: 2,
                py: 0.75,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                opacity: hasModelAccess ? 1 : 0.5,
                pointerEvents: hasModelAccess ? 'auto' : 'none',
                cursor: hasModelAccess ? 'pointer' : 'default',
                transition: 'background-color 0.2s',
                '&:hover': {
                  bgcolor: hasModelAccess 
                    ? (currentFeature === feature.id ? BOSSARD_BLUE : BOSSARD_BLUE_10)
                    : 'transparent',
                },
              }}
              onClick={() => handleFeatureSelect(feature)}
            >
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography sx={{ mr: 1, fontSize: 20 }}>
                  {feature.icon}
                </Typography>
                <ListItemText
                  primary={feature.name}
                  primaryTypographyProps={{            
                    fontSize: 14,
                    fontWeight: currentFeature === feature.id ? 600 : 400,
                    noWrap: true,
                    color: currentFeature === feature.id ? '#ffffff' : 'text.primary'
                  }}
                />
              </Box>
            </ListItem>
          ))}
        </List>
      )}
      <Box sx={{ borderBottom: '1px solid rgba(255, 255, 255, 0.1)', my: 2 }} />
    </Box>
  );
};

const Sidebar: React.FC<SidebarProps> = ({ user, hasModelAccess = true }) => {
  const queryClient = useQueryClient();

  const {
    conversationList,
    selectedConversation,
    setSelectedConversation,
    sidebarOpen,
    setSidebarOpen,
    accordionState,
    setAccordionState,
    agentList,
    selectedAgent,
    setSelectedAgent,
    setConversationList,
    setCurrentFeature,
    currentFeature,
    setSelectedModel,
    setDataSources,
    modelList,
    userSettings,
    messages
  } = useAppStore();

  const { data: queriedConversations } = useConversationsQuery();

  const refreshConversations = useCallback(async () => {
    await invalidateConversationCollections(queryClient);
    return queryClient.fetchQuery({
      queryKey: queryKeys.conversations.list(),
      queryFn: listConversations,
      staleTime: 0,
    });
  }, [queryClient]);

  useEffect(() => {
    if (queriedConversations) {
      setConversationList(queriedConversations);
    }
  }, [queriedConversations, setConversationList]);
  
  // Check if device is mobile (small screen)
  // Initialize with actual window width to avoid flash of wrong layout
  const [isMobile, setIsMobile] = useState(() => {
    return typeof window !== 'undefined' && window.innerWidth < 768;
  });
  
  // Detect mobile device based on screen width
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768); // 768px is typical mobile breakpoint
    };
    
    // Run check immediately to ensure correct state
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  
  // Initialize sidebar state from localStorage on mount (only on desktop)
  useEffect(() => {
    if (isMobile) {
      // On mobile, always start closed
      setSidebarOpen(false);
    }
    // On desktop, sidebar state comes from backend via setUserSettings in store
  }, [setSidebarOpen, isMobile]);

  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [menuType, setMenuType] = useState<'recents' | 'favorites' | null>(null);
  const [menuConversation, setMenuConversation] = useState<Conversation | null>(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [conversationToDelete, setConversationToDelete] = useState<Conversation | null>(null);
  const [renameDialogOpen, setRenameDialogOpen] = useState(false);
  const [conversationToRename, setConversationToRename] = useState<Conversation | null>(null);
  const [newConversationTitle, setNewConversationTitle] = useState('');
  
  const handleToggleSidebar = () => {
    const newState = !sidebarOpen;
    setSidebarOpen(newState);
    // Persistence to backend is handled in the store's setSidebarOpen
  };  useEffect(() => {
    setAccordionState('agents', false);
    setAccordionState('recents', true);
    // Auto-open Features section if a feature is selected on load
    setAccordionState('features', !!currentFeature);
  }, []);

  // Guard against concurrent handleNewChat invocations (double-click, rapid keypresses,
  // or race between App.tsx initialisation and user interaction).
  const isCreatingChatRef = useRef(false);

  const handleNewChat = async () => {
    if (isCreatingChatRef.current) return;
    isCreatingChatRef.current = true;
    try {
      const previousConversationId = selectedConversation?.id;
      const shouldResetAgentToDefault = selectedAgent === 'researcher';
      
      // Check if current conversation has messages in the UI (more reliable than backend data)
      const currentConversationHasMessages = messages && messages.length > 0;
      
      // If we're already in an empty chat, just stay in it
      if (!currentConversationHasMessages && previousConversationId) {
        // Refresh timestamp of current empty chat
        const updates: Partial<Conversation> = {
          title: formatNewChatTitle(),
          updated_date: new Date().toISOString()
        };
        if (shouldResetAgentToDefault) {
          updates.agent_key = 'default';
        }
        await updateConversation(previousConversationId, updates);
        
        // Refresh conversation list to update the sorting
        const updatedConversations = await refreshConversations();
        setConversationList(updatedConversations);

        // Keep selected conversation in sync with backend updates (title/agent_key).
        const refreshedCurrentConversation = updatedConversations.find(
          conv => conv.id === previousConversationId
        );
        if (refreshedCurrentConversation) {
          setSelectedConversation(refreshedCurrentConversation);
        }

        if (shouldResetAgentToDefault) {
          setSelectedAgent(null);
          setCurrentFeature(null);
          setDataSources({ ...DEFAULT_DATA_SOURCES });
        }
        
        return; // Stay in current empty chat
      }
      
      // Current chat has messages - look for any empty chat to reuse
      const freshConversations = await refreshConversations();
      
      // Find empty chats (excluding current one)
      // Sort by updated_date descending (newest first) to prefer reusing the most recent empty chat
      // Use message_count (actual count from backend) instead of messages_ids (which is not maintained)
      const emptyChat = freshConversations
        .filter(conv => 
          conv.id !== previousConversationId && 
          (conv.message_count === 0 || conv.message_count === undefined)
        )
        .sort((a, b) => {
          // Sort by updated_date descending (newest first)
          const dateA = new Date(a.updated_date || a.created_date).getTime();
          const dateB = new Date(b.updated_date || b.created_date).getTime();
          return dateB - dateA;
        })[0]; // Get the most recent empty chat
      
      let conversationId: string;
      
      if (emptyChat) {
        // Found an empty chat - refresh timestamp so it behaves like a newly opened chat.
        const updates: Partial<Conversation> = {
          title: formatNewChatTitle(),
          updated_date: new Date().toISOString()
        };
        if (shouldResetAgentToDefault) {
          updates.agent_key = 'default';
        }
        await updateConversation(emptyChat.id, updates);
        conversationId = emptyChat.id;
      } else {
        // No empty chat found - create a new one
        const newConversation = await createConversation({ title: formatNewChatTitle() });
        conversationId = newConversation.id;
      }
      
      // Small delay to ensure backend has persisted the conversation
      await new Promise(resolve => setTimeout(resolve, 100));
      
      // Refresh conversation list from backend to ensure correct sorting by updated_date
      const updatedConversations = await refreshConversations();
      setConversationList(updatedConversations);
      
      // Find and select the conversation from the refreshed list
      const conversation = updatedConversations.find(c => c.id === conversationId);
      if (conversation) {
        // Clear messages before switching to ensure we don't see old messages
        useAppStore.setState({ messages: [] });
        setSelectedConversation(conversation);
      }
      
      setSelectedAgent(null);
      setCurrentFeature(null); // Clear any selected feature
      setDataSources({ ...DEFAULT_DATA_SOURCES }); // Reset data sources for new chat
      
      // Set model to user's default_model (last used model globally)
      const defaultModel = userSettings?.default_model;
      if (defaultModel) {
        const isModelAccessible = modelList.some(m => m.id === defaultModel);
        if (isModelAccessible) {
          setSelectedModel(defaultModel);
        }
      }
      
      // Auto-close sidebar on mobile after creating new chat
      if (isMobile) {
        setSidebarOpen(false);
      }
      
      // Update title of the PREVIOUS conversation in the background (non-blocking)
      if (previousConversationId && previousConversationId !== conversationId) {
        // Fire and forget - don't await this.
        // Refresh conversations only after select finishes so generated titles
        // become visible without blocking navigation.
        selectConversation(conversationId, previousConversationId)
          .then(async () => {
            const refreshedConversations = await refreshConversations();
            setConversationList(refreshedConversations);
          })
          .catch((error) => {
            console.error('Failed to update previous conversation title:', error);
          });
      }
    } catch (error) {
      console.error('Failed to create new conversation:', error);
    } finally {
      isCreatingChatRef.current = false;
    }
  };

  const handleConversationSelect = async (conversation: Conversation) => {
    try {
      const previousConversationId = selectedConversation?.id;
      
      // Switch to the new conversation immediately for better UX
      setSelectedConversation(conversation);
      setCurrentFeature(null); // Clear any selected feature
      
      // Set model from conversation's last_used_model (with fallback)
      if (conversation.last_used_model) {
        // Check if the model is still accessible
        const isModelAccessible = modelList.some(m => m.id === conversation.last_used_model);
        if (isModelAccessible) {
          setSelectedModel(conversation.last_used_model);
        } else {
          // Model no longer accessible, fall back to first available or default
          const fallbackModel = userSettings?.default_model || modelList[0]?.id;
          if (fallbackModel) {
            setSelectedModel(fallbackModel);
          }
        }
      }

      // dataSources are now restored automatically inside setSelectedConversation (store/index.ts).
      // No manual setDataSources call needed here.

      // Auto-close sidebar on mobile after selection
      if (isMobile) {
        setSidebarOpen(false);
      }
      
      // Update title of the DESELECTED conversation in the background (non-blocking)
      if (previousConversationId && previousConversationId !== conversation.id) {
        // Fire and forget - don't await this.
        // Refresh conversations only after select finishes so generated titles
        // become visible without delaying the conversation switch.
        selectConversation(conversation.id, previousConversationId)
          .then(async () => {
            const refreshedConversations = await refreshConversations();
            setConversationList(refreshedConversations);
          })
          .catch((error) => {
            console.error('Failed to update deselected conversation title:', error);
          });
      }
    } catch (error) {
      console.error('Failed to select conversation:', error);
    }
  };

  const handleMenuClose = useCallback(() => {
    setMenuAnchorEl(null);
    setMenuType(null);
    setMenuConversation(null);
  }, []);

  const handleDeleteRequest = useCallback((conversation: Conversation) => {
    setConversationToDelete(conversation);
    setShowDeleteDialog(true);
  }, []);

  // Direct action handlers for inline icons
  const handleRenameConversation = useCallback(async (conversation: Conversation) => {
    setConversationToRename(conversation);
    setNewConversationTitle(conversation.title);
    setRenameDialogOpen(true);
  }, []);

  const handleRenameConfirm = useCallback(async () => {
    if (conversationToRename && newConversationTitle && newConversationTitle !== conversationToRename.title) {
      try {
        await updateConversation(conversationToRename.id, { title: newConversationTitle });
        const updatedConversations = await refreshConversations();
        setConversationList(updatedConversations);
      } catch (error) {
        console.error('Error renaming conversation:', error);
      }
    }
    setRenameDialogOpen(false);
    setConversationToRename(null);
    setNewConversationTitle('');
  }, [conversationToRename, newConversationTitle]);

  const handleRenameCancel = useCallback(() => {
    setRenameDialogOpen(false);
    setConversationToRename(null);
    setNewConversationTitle('');
  }, []);

  const handleToggleFavorite = useCallback(async (conversation: Conversation) => {
    try {
      await updateConversation(conversation.id, { is_favourite: !conversation.is_favourite });
      const updatedConversations = await refreshConversations();
      setConversationList(updatedConversations);
    } catch (error) {
      console.error('Error toggling favorite:', error);
    }
  }, []);

  const navigate = useNavigate();
  
  const handleShowAllHistory = useCallback(() => {
    navigate('/chat-history');
  }, [navigate]);

  const handleMenuAction = async (action: string) => {
    if (!menuConversation) return;
    
    try {
      switch (action) {
        case 'add-favorite':
          await updateConversation(menuConversation.id, { is_favourite: true });
          break;
        case 'remove-favorite':
          await updateConversation(menuConversation.id, { is_favourite: false });
          break;
        case 'rename':
          const newTitle = prompt('Enter new title:', menuConversation.title);
          if (newTitle) {
            await updateConversation(menuConversation.id, { title: newTitle });
          }
          break;
        case 'delete':
          // Show confirmation dialog instead of immediately deleting
          // Use flushSync to ensure menu closes before dialog opens
          flushSync(() => {
            handleMenuClose(); // Close the menu first
          });
          handleDeleteRequest(menuConversation); // Open dialog after menu is closed
          return; // Don't proceed with deletion yet
      }
      const updatedConversations = await refreshConversations();
      setConversationList(updatedConversations);
    } catch (error) {
      console.error('Error performing menu action:', error);
    }
    handleMenuClose();
  };

  // Toggle Agents accordion; do not perform side-effects that update the
  // messages container or change selection state. The View all control should
  // only open/close the section.
  const handleToggleAgentsSection = () => {
    if (!hasModelAccess) return;
    setAccordionState('agents', !accordionState.agents);
  };

  const sortByDate = (a: Conversation, b: Conversation) => {
    const dateA = new Date(a.updated_date || a.created_date || 0).getTime();
    const dateB = new Date(b.updated_date || b.created_date || 0).getTime();
    return dateB - dateA;
  };

  // Show recents including favorites so a favorited chat appears in both sections
  const recents = conversationList
    .slice()
    .sort(sortByDate);

  const favorites = conversationList
    .filter((c) => c.is_favourite)
    .sort(sortByDate);

  const handleConfirmDelete = async () => {
    if (!conversationToDelete) return;
    
    try {
      await deleteConversation(conversationToDelete.id);
      
      // Update conversation list first
      const updatedConversations = await refreshConversations();
      setConversationList(updatedConversations);
      
      if (selectedConversation?.id === conversationToDelete.id) {
        // If we're deleting the currently selected conversation,
        // automatically create and select a new conversation
        // Add a small delay to prevent race conditions
        setTimeout(async () => {
          try {
            const newConversation = await createConversation({ 
              title: formatNewChatTitle() 
            });
            setSelectedConversation(newConversation);
          } catch (error) {
            console.error('Failed to create new conversation after deletion:', error);
            // Fallback to no selection if creation fails
            setSelectedConversation(null);
          }
        }, 100); // Small delay to ensure deletion is fully processed
      }
    } catch (error) {
      console.error('Error deleting conversation:', error);
    }
    
    setShowDeleteDialog(false);
    setConversationToDelete(null);
  };

  const handleCancelDelete = () => {
    setShowDeleteDialog(false);
    setConversationToDelete(null);
  };
  
  return (
    <>
      {/* Mobile overlay - only shown on mobile when sidebar is open */}
      {isMobile && sidebarOpen && (
        <Box
          sx={{
            position: 'fixed',
            top: 56, // Start below header (56px)
            left: 0,
            right: 0,
            bottom: 'env(safe-area-inset-bottom)',
            bgcolor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 1100,
            backdropFilter: 'blur(4px)',
          }}
          onClick={() => setSidebarOpen(false)} // Close sidebar when clicking overlay
        />
      )}
      
      {/* Floating hamburger button - only shown on mobile when sidebar is closed */}
      {isMobile && !sidebarOpen && (
        <IconButton
          onClick={handleToggleSidebar}
          sx={{
            position: 'fixed',
            top: 68, // Below header (56px) + some padding
            left: 8,
            zIndex: 1300, // Above everything else
            bgcolor: (theme) => theme.palette.mode === 'light' ? '#eeeeee' : '#444444',
            color: 'primary.main',
            boxShadow: 3,
            '&:hover': {
              bgcolor: (theme) => theme.palette.mode === 'light' ? '#e0e0e0' : '#555555',
            },
          }}
        >
          <MenuIcon />
        </IconButton>
      )}
      
      <SidebarContainer 
        open={sidebarOpen}
        isMobile={isMobile}
      >
        {sidebarOpen ? (
        <>
        <Box 
            display="flex" 
            alignItems="center" 
            justifyContent="space-between"
            mb={1}            
            sx={{ 
              position: 'sticky', 
              top: 5,
              zIndex: 2,
              bgcolor: (theme) => theme.palette.mode === 'light' ? '#eeeeee' : '#444444', // Match sidebar background
              pb: 0.5,
            }}
          >
            {/* Hamburger menu button to close sidebar - aligned to left */}
            <Tooltip title="Collapse" placement="right" arrow>
              <span>
                <IconButton 
                  onClick={handleToggleSidebar} 
                  sx={{ color: 'primary.main', left: 3, top: 0 }}
                  size="small"
                >
                  <MenuIcon />
                </IconButton>
              </span>
            </Tooltip>
            
            {/* Search button - centered */}
            <Tooltip title="Search conversations" placement="bottom" arrow>
              <Button
                variant="outlined"
                startIcon={<Search />}
                sx={{ 
                  borderRadius: 8, 
                  boxShadow: 1, 
                  textTransform: 'none', 
                  fontWeight: 600,
                  minWidth: 100
                }}
                onClick={() => navigate('/chat-history')}
              >
                Search
              </Button>
            </Tooltip>
            
            {/* New Chat button - right aligned */}
            <Tooltip title="New Chat" placement="bottom" arrow>
              <span>
                <Button
                  variant="text"
                  sx={{ 
                    borderRadius: 8,
                    textTransform: 'none', 
                    fontWeight: 600,
                    opacity: hasModelAccess ? 1 : 0.5,
                    minWidth: 'auto',
                    padding: 1,
                    color: '#007ac3',
                    backgroundColor: 'transparent',
                    border: 'none',
                    '&:hover': {
                      backgroundColor: 'transparent',
                      border: 'none'
                    }
                  }}
                  onClick={handleNewChat}
                  disabled={!hasModelAccess}
                >
                  <Add />
                </Button>
              </span>
            </Tooltip>
          </Box>
          
          {/* Scrollable content area */}
          <Box 
            sx={{ 
              flex: 1,
              overflowY: 'auto',
              // Subtract header/footer and iOS safe-area inset when on mobile to avoid
              // content appearing under the browser chrome/home indicator.
              height: isMobile ? 'calc(100% - 140px - env(safe-area-inset-bottom))' : 'calc(100% - 140px)',
              '&::-webkit-scrollbar': {
                width: '5px',
              },
              '&::-webkit-scrollbar-track': {
                background: 'rgba(0, 0, 0, 0.1)',
              },
              '&::-webkit-scrollbar-thumb': {
                background: 'rgba(255, 255, 255, 0.35)',  // Updated to match
                borderRadius: '10px',
                '&:hover': {
                  background: 'rgba(255, 255, 255, 0.45)'  // Updated hover state
                }
              }
            }}
          >
            <ConversationSection
              type="recents"
              conversations={recents}
              isOpen={accordionState.recents}
              onToggle={() => hasModelAccess && setAccordionState('recents', !accordionState.recents)}
              selectedConversation={selectedConversation}
              onSelect={(conversation) => hasModelAccess && handleConversationSelect(conversation)}
              onRename={handleRenameConversation}
              onDelete={handleDeleteRequest}
              onToggleFavorite={handleToggleFavorite}
              hasModelAccess={hasModelAccess}
              showAllHistoryLink={true}
              onShowAllHistory={handleShowAllHistory}
            />

            <ConversationSection
              type="favorites"
              conversations={favorites}
              isOpen={accordionState.favorites}
              onToggle={() => hasModelAccess && setAccordionState('favorites', !accordionState.favorites)}
              selectedConversation={selectedConversation}
              onSelect={(conversation) => hasModelAccess && handleConversationSelect(conversation)}
              onRename={handleRenameConversation}
              onDelete={handleDeleteRequest}
              onToggleFavorite={handleToggleFavorite}
              hasModelAccess={hasModelAccess}
            />

            {agentList && agentList.length > 0 && (
              <AgentSection 
                agents={agentList}
                isOpen={accordionState.agents || false}
                onToggle={handleToggleAgentsSection}
                selectedAgent={selectedAgent}
                onSelectAgent={(agent) => {
                  if (hasModelAccess) {
                    setSelectedAgent(agent.id);
                    // Auto-close sidebar on mobile after selecting agent
                    if (isMobile) {
                      setSidebarOpen(false);
                    }
                  }
                }}
                hasModelAccess={hasModelAccess}
              />
            )}
              
            <FeaturesSection
              isOpen={accordionState.features}
              onToggle={() => hasModelAccess && setAccordionState('features', !accordionState.features)}
              user={user || undefined}
              hasModelAccess={hasModelAccess}
              onFeatureSelect={() => {
                // Auto-close sidebar on mobile after selecting feature
                if (isMobile) {
                  setSidebarOpen(false);
                }
              }}
            />
          </Box>
          
          {/* Disclaimer text at bottom */}
          <Box
            sx={{
              px: 2,
              py: 1,
              textAlign: 'center',
              borderTop: '1px solid',
              borderColor: 'rgba(255, 255, 255, 0.1)'
            }}
          >
            <Typography
              variant="caption"
              sx={{
                fontSize: '0.75rem',
                color: 'text.secondary',
                display: 'block'
              }}
            >
              AI-generated content may be incorrect
            </Typography>
          </Box>
        </>      ) : (
        <Box 
          display="flex" 
          flexDirection="column" 
          alignItems="center" 
          height="100%" 
        >
          <Box 
            sx={{ 
              position: 'sticky', 
              top: 0, 
              zIndex: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              width: '100%',
              pt: 1,
              pb: 1,
              bgcolor: (theme) => theme.palette.mode === 'light' ? '#eeeeee' : '#444444', // Match sidebar background
            }}
          >
            {/* Hamburger menu button to open sidebar */}
            <Tooltip title="Expand" placement="right" arrow>
              <span>
                <IconButton 
                  onClick={handleToggleSidebar} 
                  sx={{ left: 3, top: 0, color: 'primary.main', mb: 1 }}
                  size="small"
                >
                  <MenuIcon />
                </IconButton>
              </span>
            </Tooltip>
            <Divider sx={{ width: '80%', mb: 1 }} />
            <Tooltip title="Search" placement="right" arrow>
              <span>
                <IconButton 
                  color="primary" 
                  onClick={() => navigate('/chat-history')} 
                  sx={{ left: 3, mb: 1 }}
                >
                  <Search />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="New Chat" placement="right" arrow>
              <span>
                <IconButton 
                  onClick={handleNewChat} 
                  sx={{ 
                    left: 3, 
                    opacity: hasModelAccess ? 1 : 0.5,
                    color: '#007ac3',
                    backgroundColor: 'transparent',
                    border: 'none',
                    '&:hover': {
                      backgroundColor: 'transparent',
                      border: 'none'
                    }
                  }}
                  disabled={!hasModelAccess}
                >
                  <Add />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        </Box>
      )}<Menu 
        anchorEl={menuAnchorEl} 
        open={Boolean(menuAnchorEl)} 
        onClose={handleMenuClose}
        PaperProps={{ sx: commonMenuStyles }}
      >
        {menuType === 'recents' ? (
          <>
            <MenuItem onClick={() => handleMenuAction('add-favorite')}>Add to favorites</MenuItem>
            <MenuItem onClick={() => handleMenuAction('rename')}>Rename title</MenuItem>
            <MenuItem onClick={() => handleMenuAction('delete')}>Delete chat</MenuItem>
          </>
        ) : (
          <>
            <MenuItem onClick={() => handleMenuAction('remove-favorite')}>Remove from favorites</MenuItem>
            <MenuItem onClick={() => handleMenuAction('rename')}>Rename title</MenuItem>
            <MenuItem onClick={() => handleMenuAction('delete')}>Delete chat</MenuItem>
          </>
        )}
      </Menu>

      <Suspense fallback={null}>
        <DeleteConfirmationDialog
          open={showDeleteDialog}
          chatTitle={conversationToDelete?.title || ''}
          isFavorite={Boolean(conversationToDelete?.is_favourite)}
          onConfirm={handleConfirmDelete}
          onClose={handleCancelDelete}
        />
      </Suspense>

      {/* Rename Dialog */}
      <Dialog
        open={renameDialogOpen}
        onClose={handleRenameCancel}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            minHeight: 200,
            bgcolor: (theme) => theme.palette.mode === 'light' ? 'background.paper' : 'background.paper',
          }
        }}
      >
        <DialogTitle sx={{ pb: 1, fontWeight: 600 }}>
          Rename Chat
        </DialogTitle>
        
        <DialogContent sx={{ pb: 2 }}>
          <Box>
            <Typography variant="body1" sx={{ mb: 2 }}>
              Enter a new title for{' '}
              <Typography component="span" sx={{ fontWeight: 600 }}>
                {conversationToRename?.title}
              </Typography>
            </Typography>
            
            <TextField
              autoFocus
              fullWidth
              label="Chat Title"
              value={newConversationTitle}
              onChange={(e) => setNewConversationTitle(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter' && newConversationTitle.trim() && newConversationTitle !== conversationToRename?.title) {
                  e.preventDefault();
                  handleRenameConfirm();
                }
              }}
              variant="outlined"
            />
          </Box>
        </DialogContent>
        
        <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
          <Button
            onClick={handleRenameCancel}
            variant="outlined"
            color="primary"
          >
            Cancel
          </Button>
          <Button
            onClick={handleRenameConfirm}
            variant="contained"
            color="primary"
            disabled={!newConversationTitle.trim() || newConversationTitle === conversationToRename?.title}
            sx={{
              bgcolor: (theme) => theme.palette.primary.main,
              '&:hover': {
                bgcolor: (theme) => theme.palette.primary.dark,
              }
            }}
          >
            Rename
          </Button>
        </DialogActions>
      </Dialog>
    </SidebarContainer>
    </>
  );
};

export default Sidebar;